import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { getConsent, saveConsent, type ConsentChoices } from "./CookieConsent";
import { loadDailyEnergy } from "../lib/energyTracker";
import { deleteAccountFn } from "../lib/subscription";
import { loadNudgePreferences } from "../lib/nudges";

export default function ProfilePrivacyTab() {
  const { user } = useAuth();
  const { stats } = useUserStats();
  const [cookieConsent, setCookieConsent] = useState<ConsentChoices | null>(getConsent);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const updateAnalyticsConsent = (enabled: boolean) => {
    const newConsent: ConsentChoices = {
      essential: true,
      analytics: enabled,
      timestamp: Date.now(),
    };
    saveConsent(newConsent);
    setCookieConsent(newConsent);
    if (enabled !== cookieConsent?.analytics) {
      window.location.reload();
    }
  };

  const handleExportData = () => {
    if (!user || !stats) return;

    const exportData = {
      exportedAt: new Date().toISOString(),
      account: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
      },
      statistics: {
        totalQueries: stats.totalQueries || 0,
        totalMealsAnalyzed: stats.totalMealsAnalyzed || 0,
        totalRecipesAnalyzed: stats.totalRecipesAnalyzed || 0,
        totalMenusDecoded: stats.totalMenusDecoded || 0,
        totalGroceryListsOptimized: stats.totalGroceryListsOptimized || 0,
        totalPhotosAnalyzed: stats.totalPhotosAnalyzed || 0,
        streakDays: stats.streakDays || 0,
        badges: stats.badges || [],
        joinedAt: stats.joinedAt,
        lastActiveDate: stats.lastActiveDate,
      },
      preferences: {
        dietType: stats.dietType,
        allergies: stats.allergies,
        nudgePreferences: loadNudgePreferences(),
        cookieConsent: cookieConsent,
      },
      energyTracking: loadDailyEnergy(),
      topFoodsSearched: stats.topFoodsSearched || [],
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `glycemicai-data-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDeleteAccount = async () => {
    if (!user || deleteConfirmText !== "DELETE") return;

    setDeleting(true);
    try {
      await deleteAccountFn();

      localStorage.removeItem(`terms_accepted_${user.uid}`);
      localStorage.removeItem("cookie_consent");
      localStorage.removeItem("nudge_preferences");
      localStorage.removeItem("energy_tracker_data");
      localStorage.removeItem("last_nudge_time");
      localStorage.removeItem("dismissed_nudges");

      window.location.href = "/";
    } catch (err) {
      console.error("Failed to delete account:", err);
      alert("Failed to delete account. Please try again or contact support.");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <div className="mb-8">
        <div className="section-label">Privacy & Data</div>
        <p className="text-sm text-muted mb-4">
          Manage your privacy settings and access your data.
        </p>

        {/* Analytics Consent */}
        <div className="mb-6">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => updateAnalyticsConsent(!cookieConsent?.analytics)}
          >
            <div>
              <div className="text-sm text-text font-[family-name:--font-ui] font-medium">Analytics</div>
              <div className="text-xs text-muted font-[family-name:--font-ui]">Help improve GlycemicAI by sharing anonymous usage data</div>
            </div>
            <div
              className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${
                cookieConsent?.analytics ? "bg-accent" : "bg-muted/30"
              }`}
            >
              <div
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
                  cookieConsent?.analytics ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </div>
          </div>
        </div>

        {/* Export Data */}
        <div className="border-t border-border-light pt-4 mb-4">
          <p className="text-sm text-text font-[family-name:--font-ui] font-medium mb-1">Export your data</p>
          <p className="text-xs text-muted font-[family-name:--font-ui] mb-3">
            Download a copy of all your data including preferences, statistics, and tracked meals.
          </p>
          <button
            onClick={handleExportData}
            className="px-4 py-2 border border-border rounded-md font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 text-text"
          >
            Download My Data
          </button>
        </div>

        {/* Delete Account */}
        <div className="border-t border-border-light pt-4 mb-4">
          <p className="text-sm text-gi-high font-[family-name:--font-ui] font-medium mb-1">Delete account</p>
          <p className="text-xs text-muted font-[family-name:--font-ui] mb-3">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-sm text-gi-high font-[family-name:--font-ui] font-medium hover:underline"
            >
              Delete My Account
            </button>
          ) : (
            <div className="border border-border rounded-md p-4">
              <p className="text-sm text-text font-[family-name:--font-ui] mb-3">
                Are you sure? Type <strong>DELETE</strong> to confirm.
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full px-3 py-2 border border-border rounded-md text-sm font-[family-name:--font-ui] mb-3 focus:outline-none focus:border-accent bg-bg text-text"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                  className="flex-1 px-4 py-2 border border-border rounded-md font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 text-text"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== "DELETE" || deleting}
                  className="flex-1 px-4 py-2 bg-gi-high text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? "Deleting..." : "Delete Forever"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
